import { useQuery } from "@apollo/client";
import { useMemo, useContext } from "react";
import { GET_OPPORTUNITY_STATUS } from "../graphql/query";
import { UserContext } from "../amplify/authenticator-provider";
import { isAllLocationSelected } from "../library/helpers";

/**
 * Custom hook to fetch and organize opportunity statuses.
 * Handles location logic automatically and provides data in multiple formats.
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.fetchAll - Whether to fetch all statuses (default: false)
 * @param {number} options.opportunityTypeId - Filter by opportunity type ID
 * @param {boolean} options.skip - Skip query execution
 * @returns {Object} - Contains statuses in various formats and query states
 */
const useOpportunityStatus = (options = {}) => {
  const { fetchAll = true, opportunityTypeId, skip = false } = options;
  const { user } = useContext(UserContext);
  const isAllLocation = isAllLocationSelected();

  // Get location IDs based on current selection
  const getLocationIds = () => {
    const currentLocationId = sessionStorage?.getItem("currentLocationId");
    if (isAllLocation && currentLocationId) {
      // If all locations selected, parse comma-separated IDs
      return currentLocationId.split(",").map(id => parseInt(id.trim(), 10));
    }
    if (currentLocationId) {
      // If single location, return as array with single element
      return [parseInt(currentLocationId, 10)];
    }
    return [];
  };

  const locationIds = getLocationIds();

  // Query to fetch opportunity statuses
  const { data, loading, error, refetch } = useQuery(GET_OPPORTUNITY_STATUS, {
    variables: {
      companyId: user?.company?.id,
      locationIds,
      fetchAll,
      ...(opportunityTypeId && { opportunityTypeId }),
    },
    skip: skip || !user?.company?.id || locationIds.length === 0,
    fetchPolicy: "cache-first",
  });

  // Raw statuses from API
  const statuses = useMemo(() => {
    return data?.getOpportunityStatus || [];
  }, [data]);

  // Flat list of all statuses
  const flatStatuses = useMemo(() => {
    return statuses.map(status => ({
      id: String(status.id), // Normalize to string
      name: status.name,
      parentStatusId: status.parentStatusId,
      opportunityType: status.opportunityType,
      companyId: status.companyId,
      locationId: status.locationId,
    }));
  }, [statuses]);

  // Build hierarchical tree structure
  const hierarchicalStatuses = useMemo(() => {
    if (!statuses.length) return [];

    const statusMap = new Map();
    const rootStatuses = [];

    // First pass: create a map of all statuses
    // Normalize IDs to strings for consistent lookup
    statuses.forEach(status => {
      statusMap.set(String(status.id), {
        ...status,
        id: String(status.id), // Ensure ID is string
        children: [],
      });
    });

    // Second pass: build the tree
    statuses.forEach(status => {
      const statusNode = statusMap.get(String(status.id));
      if (status.parentStatusId !== null && status.parentStatusId !== undefined) {
        // Look up parent using normalized string ID
        const parent = statusMap.get(String(status.parentStatusId));
        if (parent) {
          parent.children.push(statusNode);
        } else {
          // Parent not found, treat as root
          rootStatuses.push(statusNode);
        }
      } else {
        // No parent, this is a root status
        rootStatuses.push(statusNode);
      }
    });

    return rootStatuses;
  }, [statuses]);

  // Group statuses by opportunity type
  const statusesByType = useMemo(() => {
    const grouped = {};

    statuses.forEach(status => {
      const typeId = status.opportunityType?.id;
      const typeName = status.opportunityType?.name || "Uncategorized";

      if (!grouped[typeId]) {
        grouped[typeId] = {
          id: typeId,
          name: typeName,
          statuses: [],
        };
      }

      grouped[typeId].statuses.push(status);
    });

    return Object.values(grouped);
  }, [statuses]);

  // Format for Ant Design table filters
  const tableFilters = useMemo(() => {
    return flatStatuses.map(status => ({
      text: status.name,
      value: status.id,
    }));
  }, [flatStatuses]);

  // Format for Ant Design select options
  const selectOptions = useMemo(() => {
    return flatStatuses.map(status => ({
      label: status.name,
      value: status.id,
    }));
  }, [flatStatuses]);

  // Build a status map for quick path lookup
  const statusMap = useMemo(() => {
    const map = {};
    statuses.forEach(status => {
      map[status.id] = status;
    });
    return map;
  }, [statuses]);

  /**
   * Get the full path of a status (e.g. "Parent > Child > Grandchild")
   * @param {number|string} statusId - The status ID to get the path for
   * @param {string} joinBy - The separator between path segments (default: ">")
   * @returns {string} The full status path
   */
  const getStatusPath = (statusId, joinBy = ">") => {
    if (!statuses.length || !statusId) return "";
    if (!statusMap[statusId]) return "";

    const path = [];
    let currentStatus = statusMap[statusId];
    path.push(currentStatus?.name);

    while (currentStatus?.parentStatusId) {
      currentStatus = statusMap[currentStatus.parentStatusId];
      if (!currentStatus) break;
      path.push(currentStatus.name);
    }

    return path.reverse().join(` ${joinBy} `);
  };

  /**
   * Get the root (most parent) status name for a given status ID
   * Returns null if statuses haven't loaded yet (prevents child name flash)
   * @param {number|string} statusId - The status ID
   * @returns {string|null} The root parent status name
   */
  const getRootStatusName = statusId => {
    if (!statuses.length || !statusId) return null;
    if (!statusMap[statusId]) return null;
    let current = statusMap[statusId];
    while (current?.parentStatusId && statusMap[current.parentStatusId]) {
      current = statusMap[current.parentStatusId];
    }
    return current?.name || null;
  };

  // Format for hierarchical select options with indentation
  const hierarchicalSelectOptions = useMemo(() => {
    const buildOptions = (statusNodes, level = 0) => {
      const options = [];
      statusNodes.forEach(node => {
        options.push({
          label: `${"  ".repeat(level)}${node.name}`,
          value: node.id,
          level,
        });
        if (node.children && node.children.length > 0) {
          options.push(...buildOptions(node.children, level + 1));
        }
      });
      return options;
    };

    return buildOptions(hierarchicalStatuses);
  }, [hierarchicalStatuses]);

  return {
    // Raw data
    statuses,

    // Formatted data
    flatStatuses,
    hierarchicalStatuses,
    statusesByType,

    // UI-ready formats
    tableFilters,
    selectOptions,
    hierarchicalSelectOptions,

    // Query states
    loading,
    error,
    refetch,

    // Utilities
    getStatusPath,
    getRootStatusName,
    isAllLocation,
    locationIds,
  };
};

export default useOpportunityStatus;
